import type React from 'react';
import { useRef, useState } from 'react';
import { adminApi, CSV_TEMPLATE_URL, type CsvImportSummary } from '@/admin/adminApi';

export function AdminImportPage() {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [csvText, setCsvText] = useState('');
  const [fileName, setFileName] = useState('');
  const [status, setStatus] = useState<'idle' | 'loading' | 'done' | 'error'>('idle');
  const [summary, setSummary] = useState<CsvImportSummary | null>(null);
  const [errorMessage, setErrorMessage] = useState('');

  function handleFileChange(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (!file) return;
    setFileName(file.name);
    const reader = new FileReader();
    reader.onload = () => setCsvText(String(reader.result ?? ''));
    reader.readAsText(file);
  }

  async function handleImport() {
    if (!csvText.trim()) return;
    setStatus('loading');
    setErrorMessage('');
    const res = await adminApi.importCsv(csvText);
    if (!res.success) {
      setStatus('error');
      setErrorMessage(res.message);
      return;
    }
    setSummary(res.data);
    setStatus('done');
  }

  return (
    <div className="max-w-3xl space-y-6">
      <div>
        <h2 className="text-lg font-semibold text-ink">Import a size chart from CSV</h2>
        <p className="mt-1 text-sm text-muted">
          Imported rows always enter review state — nothing becomes visible to shoppers until you
          verify the resulting chart on the Charts tab.
        </p>
      </div>

      <a
        href={CSV_TEMPLATE_URL}
        className="inline-block rounded-xl border border-stone-200 bg-white px-4 py-2 text-sm font-medium text-brand shadow-sm"
      >
        Download CSV template
      </a>

      <div className="rounded-2xl border border-dashed border-stone-300 bg-white p-6 text-center">
        <input
          ref={fileInputRef}
          type="file"
          accept=".csv,text/csv"
          onChange={handleFileChange}
          className="mx-auto block text-sm"
        />
        {fileName && <p className="mt-2 text-sm text-muted">Selected: {fileName}</p>}
      </div>

      <button
        type="button"
        onClick={handleImport}
        disabled={!csvText.trim() || status === 'loading'}
        className="min-h-[44px] rounded-xl bg-brand px-6 py-2.5 font-semibold text-ink shadow-card disabled:opacity-40"
      >
        {status === 'loading' ? 'Validating and importing…' : 'Validate and import'}
      </button>

      {status === 'error' && (
        <p role="alert" className="rounded-xl bg-danger/10 p-3 text-sm text-danger">
          {errorMessage}
        </p>
      )}

      {status === 'done' && summary && (
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
            <Stat label="Rows in file" value={summary.totalRowsInFile} />
            <Stat label="Imported (pending review)" value={summary.rowsImported} accent="brand" />
            <Stat label="Skipped" value={summary.rowsSkipped} accent={summary.rowsSkipped > 0 ? 'danger' : undefined} />
            <Stat label="Charts touched" value={summary.chartsTouched} />
          </div>

          {summary.issues.length > 0 && (
            <div className="overflow-x-auto rounded-2xl border border-stone-200">
              <table className="w-full text-left text-sm">
                <thead className="bg-stone-50 text-xs uppercase text-muted">
                  <tr>
                    <th className="px-3 py-2">Row</th>
                    <th className="px-3 py-2">Column</th>
                    <th className="px-3 py-2">Issue</th>
                    <th className="px-3 py-2">Message</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-stone-100">
                  {summary.issues.map((issue, i) => (
                    <tr key={i}>
                      <td className="px-3 py-2 font-mono">{issue.row}</td>
                      <td className="px-3 py-2 font-mono text-muted">{issue.column ?? '—'}</td>
                      <td className="px-3 py-2">
                        <span className="rounded-full bg-danger/10 px-2 py-0.5 text-xs font-medium text-danger">
                          {issue.code}
                        </span>
                      </td>
                      <td className="px-3 py-2 text-ink/80">{issue.message}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function Stat({ label, value, accent }: { label: string; value: number; accent?: 'brand' | 'danger' }) {
  const color = accent === 'brand' ? 'text-brand' : accent === 'danger' ? 'text-danger' : 'text-ink';
  return (
    <div className="rounded-xl border border-stone-200 bg-white p-3">
      <p className={`text-2xl font-bold ${color}`}>{value}</p>
      <p className="text-xs text-muted">{label}</p>
    </div>
  );
}
